import { useState } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../services/client/rest";
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

function Login() {
  const [errorMessage, setErrorMessage] = useState("");
  const [usernameValue, setUsernameValue] = useState("");
  const [passwordValue, setPasswordValue] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const history = useHistory();

  const onLogInClicked = async () => {
    setIsValidating(true);
    setErrorMessage("");
    const request = {
      Username: usernameValue,
      Password: passwordValue,
    };
    let url = BASE_URL + "Auth/token";
    await axios
      .post(url, request)
      .then((response) => {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("username", response.data.userName);
        localStorage.setItem("user", JSON.stringify(response.data));
        let lastQueryType = localStorage.getItem("lastQueryType");
        let defaultUrl = "/";
        if (lastQueryType != null && lastQueryType !== "") {
          defaultUrl = "/?Qtype=" + lastQueryType;
          localStorage.removeItem("lastQueryType");
        }
        history.push(defaultUrl);
      })
      .catch((err) => {
        const errorResponse = err.response?.data || "An error occurred";
        setErrorMessage(errorResponse);
      })
      .finally(() => setIsValidating(false));
  };

  const handleKeyPress = (event: { key: string }) => {
    if (event.key === "Enter") {
      onLogInClicked();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f0f2f5",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          mx: 2,
          backgroundColor: "#fff",
          borderRadius: "12px",
          boxShadow: "0 2px 16px rgba(0,0,0,0.10)",
          border: "1px solid #e8e8e8",
          overflow: "hidden",
        }}
      >
        {/* Top accent bar */}
        <Box sx={{ height: "4px", backgroundColor: "#424949" }} />

        <Box sx={{ px: 4, pt: 4, pb: 4 }}>
          {/* Logo */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <img
              src="/CloudIcon.jpg"
              alt="CSI Cloud"
              style={{
                width: "72px",
                height: "72px",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
          </Box>

          {/* Heading */}
          <Typography
            variant="h6"
            align="center"
            sx={{ fontWeight: 700, color: "#1a1a1a", mb: 0.5 }}
          >
            CSI Cloud Admin
          </Typography>
          <Typography
            variant="body2"
            align="center"
            sx={{ color: "#888", mb: 3, fontSize: "0.85rem" }}
          >
            Enter your credentials to continue
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {/* Form */}
          <Box component="form" noValidate onKeyPress={handleKeyPress}>
            <TextField
              fullWidth
              id="username"
              label="Domain\Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={usernameValue}
              onChange={(e) => setUsernameValue(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon sx={{ color: "#bbb", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />

            <TextField
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={passwordValue}
              onChange={(e) => setPasswordValue(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ ...fieldSx, mt: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: "#bbb", fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      size="small"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: 18, color: "#bbb" }} />
                      ) : (
                        <Visibility sx={{ fontSize: 18, color: "#bbb" }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {errorMessage && (
              <Alert severity="error" sx={{ mt: 2, fontSize: "0.82rem", py: 0.5 }}>
                {errorMessage}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={onLogInClicked}
              disabled={!usernameValue || !passwordValue || isValidating}
              disableElevation
              sx={{
                mt: 3,
                py: 1.2,
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "0.9rem",
                textTransform: "none",
                backgroundColor: "#424949",
                "&:hover": { backgroundColor: "#2d3436" },
                "&:disabled": { backgroundColor: "#e0e0e0", color: "#aaa" },
              }}
            >
              {isValidating ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Sign In"
              )}
            </Button>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            backgroundColor: "#fafafa",
            borderTop: "1px solid #f0f0f0",
            py: 1.5,
            textAlign: "center",
          }}
        >
          <Typography variant="caption" sx={{ color: "#bbb", fontSize: "0.72rem" }}>
            &copy; {new Date().getFullYear()} CSI &mdash; Authorized personnel only
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fafafa",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#424949" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#424949" },
  },
  "& label.Mui-focused": { color: "#424949" },
};

export default Login;
